// Magellan Plus membership in the app (Round 3 Lane 5, R-46). Talks only to the `plus` function; card details only ever go
// into Stripe's hosted Checkout and Customer Portal pages, which this file opens. Whether someone is a member is read
// from the server (the webhook writes it), never decided here.
// Flags: plus_checkout (join with Stripe) and affiliate_links (R-47) are added to lib/flags.ts by the release lead at merge,
// default OFF. Until a flag exists in this build, the helpers below read it as off, so this file compiles on either side.
import { callFn } from './api'
import { FLAGS, flag, useFlag, type FlagName } from './flags'

/** A flag that may not be in this build's flags.ts yet: off until it is. (Stable per build, so the hook order never changes.) */
export function optionalFlag(name: string): FlagName | null {
  return name in FLAGS ? (name as FlagName) : null
}
const PLUS_CHECKOUT = optionalFlag('plus_checkout')
const AFFILIATE = optionalFlag('affiliate_links')

const never = () => false
/** plus_checkout on (QA overrides included). */
export const usePlusCheckout: () => boolean = PLUS_CHECKOUT ? () => useFlag(PLUS_CHECKOUT) : never
/** affiliate_links on (R-47). */
export const useAffiliateLinks: () => boolean = AFFILIATE ? () => useFlag(AFFILIATE) : never
export const affiliateLinksOn = (): boolean => (AFFILIATE ? flag(AFFILIATE) : false)

export type PlusPrice = { amount: number; currency: string; interval: string } | null
export type PlusStatus =
  | { on: false }
  | { on: true; member: boolean; status: string | null; cancel_at_period_end: boolean; current_period_end: string | null; can_manage: boolean; price: PlusPrice }

export const plusStatus = () => callFn<PlusStatus>('plus', { action: 'status' })

/** Leave for Stripe (Checkout to join, the Portal to manage or cancel). Same tab, so "back" returns to Settings. */
export async function goToStripe(action: 'checkout' | 'portal'): Promise<void> {
  const { url } = await callFn<{ url: string }>('plus', { action })
  // only ever Stripe's own pages (checkout.stripe.com, billing.stripe.com)
  const u = new URL(url)
  if (u.protocol !== 'https:' || !/(^|\.)stripe\.com$/.test(u.hostname)) throw new Error('unexpected payment page')
  location.assign(url)
}

/** "$10 a month" style price, from Stripe's price (cents), in the person's locale. */
export function priceText(p: PlusPrice): { price: string; interval: string } | null {
  if (!p) return null
  const price = new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency.toUpperCase(), minimumFractionDigits: p.amount % 100 ? 2 : 0 }).format(p.amount / 100)
  return { price, interval: p.interval }
}

type PlusReturn = 'joined' | 'cancelled' | 'back' | null
let taken: { href: string; v: PlusReturn } | null = null
/** ?plus=joined|cancelled|back after Stripe sends the person back; read once, then removed from the address bar (the
 *  answer is kept for this page load, so a second call, e.g. React's dev double render, gets the same value). */
export function takePlusReturn(): PlusReturn {
  if (taken && taken.href === location.pathname) return taken.v
  const v = readPlusReturn()
  taken = { href: location.pathname, v }
  return v
}
function readPlusReturn(): PlusReturn {
  try {
    const u = new URL(location.href)
    const v = u.searchParams.get('plus')
    if (!v) return null
    u.searchParams.delete('plus')
    history.replaceState(history.state, '', u.pathname + u.search + u.hash)
    return v === 'joined' || v === 'cancelled' || v === 'back' ? v : null
  } catch { return null }
}
